import { currentSubSession, onSubSessionChange } from './currentSubSession';
import { isSalesProcessKey } from './salesProcessKeys';
import { SALES_PROCESS_KEYS_OWNER_STORAGE_KEY } from './wireContract';

type KeyOwnerStorage = Pick<Storage, 'getItem' | 'key' | 'length' | 'removeItem' | 'setItem'>;

// Read on every use: merely accessing sessionStorage throws when it is blocked.
const getStorage = (): KeyOwnerStorage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const salesProcessKeysIn = (storage: KeyOwnerStorage): string[] => {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key !== null && isSalesProcessKey(key)) {
      keys.push(key);
    }
  }
  return keys;
};

const clearSalesProcessKeys = (storage: KeyOwnerStorage): void => {
  // Collected first: removing while iterating shifts the indexes.
  salesProcessKeysIn(storage).forEach((key) => {
    try {
      storage.removeItem(key);
    } catch {
      // Best effort: one key that can't be removed must not keep the others.
    }
  });
};

const reconcile = (subSessionId: string): void => {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    const ownerId = storage.getItem(SALES_PROCESS_KEYS_OWNER_STORAGE_KEY);
    if (ownerId === subSessionId) {
      return;
    }
    // No owner yet: a first visit, or keys written before the feature was on.
    // They are this tab's own, so they are kept and only claimed.
    if (ownerId !== null) {
      clearSalesProcessKeys(storage);
    }
    storage.setItem(SALES_PROCESS_KEYS_OWNER_STORAGE_KEY, subSessionId);
  } catch {
    // Blocked or full storage: nothing to protect, and never break the page.
  }
};

/**
 * Drops the booking-flow state a duplicated tab inherited, once its confirmed id differs
 * from the one the keys were written under. A change to null is only a re-claim, so it is ignored.
 */
export const installSalesProcessKeyGuard = (): (() => void) => {
  const unsubscribe = onSubSessionChange((subSessionId) => {
    if (subSessionId !== null) {
      reconcile(subSessionId);
    }
  });

  const confirmedId = currentSubSession();
  if (confirmedId !== null) {
    reconcile(confirmedId);
  }

  return unsubscribe;
};
